import { useEffect, useState } from "react";
import { Save, Youtube, ExternalLink, Key, Link as LinkIcon } from "lucide-react";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminYouTube() {
  const [config, setConfig] = useState({
    channel_url: "https://www.youtube.com/@DemoPod",
    channel_handle: "DemoPod",
    fallback_embed_url: "",
    api_key: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await api.getYouTubeConfig();
        setConfig({
          channel_url: data.channel_url || "https://www.youtube.com/@DemoPod",
          channel_handle: data.channel_handle || "DemoPod",
          fallback_embed_url: data.fallback_embed_url || "",
          api_key: data.api_key || "",
        });
      } catch (err) {
        logger.warn("YouTube config fetch failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);

    try {
      await api.updateYouTubeConfig(config);
      toast.success("YouTube settings saved successfully");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  // Convert YouTube watch URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return "";
    
    // If it's already an embed URL, return as-is
    if (url.includes("/embed/")) return url;
    
    // Extract video ID from various YouTube URL formats
    let videoId = "";
    
    if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(url.split("?")[1]);
      videoId = urlParams.get("v");
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("youtube.com/embed/")) {
      videoId = url.split("/embed/")[1]?.split("?")[0];
    }
    
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="font-mono text-white/50 animate-pulse">Loading settings...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-youtube-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            YOUTUBE SETTINGS
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Configure the DemoPod YouTube integration
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
          data-testid="save-youtube-btn"
        >
          <Save className="w-5 h-5" />
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Channel Settings */}
        <div className="bg-demolition-paper border border-white/10 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <Youtube className="w-6 h-6 text-demolition-red" />
            <h2 className="font-teko text-xl text-white uppercase tracking-widest">
              CHANNEL SETTINGS
            </h2>
          </div>

          <div className="space-y-2">
            <Label className="font-teko text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Channel URL
            </Label>
            <Input
              value={config.channel_url}
              onChange={(e) => handleChange("channel_url", e.target.value)}
              placeholder="https://www.youtube.com/@DemoPod"
              className="bg-demolition-subtle border-white/10 text-white font-mono"
              data-testid="channel-url-input"
            />
            <p className="font-mono text-white/40 text-xs">
              The full URL to the YouTube channel
            </p>
          </div>

          <div className="space-y-2">
            <Label className="font-teko text-lg text-white uppercase tracking-wider">
              Channel Handle
            </Label>
            <Input
              value={config.channel_handle}
              onChange={(e) => handleChange("channel_handle", e.target.value)}
              placeholder="DemoPod"
              className="bg-demolition-subtle border-white/10 text-white font-mono"
              data-testid="channel-handle-input"
            />
            <p className="font-mono text-white/40 text-xs">
              The @handle shown on the YouTube page
            </p>
          </div>
        </div>

        {/* Featured Video */}
        <div className="bg-demolition-paper border border-white/10 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <ExternalLink className="w-6 h-6 text-demolition-red" />
            <h2 className="font-teko text-xl text-white uppercase tracking-widest">
              FEATURED VIDEO
            </h2>
          </div>

          <div className="space-y-2">
            <Label className="font-teko text-lg text-white uppercase tracking-wider">
              Fallback Embed URL
            </Label>
            <Input
              value={config.fallback_embed_url}
              onChange={(e) => handleChange("fallback_embed_url", e.target.value)}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
              className="bg-demolition-subtle border-white/10 text-white font-mono"
              data-testid="embed-url-input"
            />
            <p className="font-mono text-white/40 text-xs">
              Paste a YouTube watch URL or embed URL. This video will be featured on the site.
            </p>
          </div>

          {/* Preview */}
          {config.fallback_embed_url && (
            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Preview
              </Label>
              <div className="aspect-video bg-demolition-subtle border border-white/10 overflow-hidden">
                <iframe
                  src={getEmbedUrl(config.fallback_embed_url)}
                  title="Video Preview"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>

        {/* API Key Status */}
        <div className="lg:col-span-2 bg-demolition-subtle border border-white/10 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <Key className="w-6 h-6 text-green-500" />
            <div>
              <h2 className="font-teko text-xl text-white uppercase tracking-widest">
                API STATUS: ACTIVE
              </h2>
              <p className="font-mono text-green-500 text-xs mt-1">
                YouTube API is configured and fetching latest videos automatically
              </p>
            </div>
          </div>

          <div className="bg-demolition-paper border border-green-500/30 p-4">
            <p className="font-mono text-white/70 text-sm">
              <strong className="text-green-500">Active:</strong> The YouTube Data API is configured and working. 
              The site automatically fetches and displays the latest video from the DemoPod channel with 30-minute caching.
              The fallback embed URL above is only used if the API becomes unavailable.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
